import type {
  GraphChangeSetWrite,
  GraphEdge,
  GraphNode,
  GraphRelationType,
  GraphVisualizationFrame
} from "@salesko/contracts";
import {
  stableRelationshipCreateReason,
  stableRelationshipDeleteReason,
  stableRelationshipEditReason,
  stableRelationshipTouchpointReason
} from "./stableText";

export type RelationshipCreateDraft = {
  evidence: string;
  label: string;
  strength: number;
  targetNodeId: string;
  type: GraphRelationType;
};

export type RelationshipEditDraft = {
  evidence: string;
  label: string;
  strength: number;
  type: GraphRelationType;
};

export const relationshipTypeOptions = [
  { value: "related_to" },
  { value: "met_at" },
  { value: "works_at" },
  { value: "attended" },
  { value: "introduced_by" },
  { value: "opportunity_at" },
  { value: "mentioned" }
] satisfies Array<{ value: GraphRelationType }>;

const stableRelationshipLabels = {
  attended: "attended",
  introduced_by: "introduced by",
  mentioned: "mentioned",
  met_at: "met at",
  opportunity_at: "opportunity at",
  related_to: "related to",
  works_at: "works at"
} satisfies Record<GraphRelationType, string>;

export function relationshipCreateDraftFromFrame(
  frame: GraphVisualizationFrame | null,
  sourceNode: GraphNode | null
): RelationshipCreateDraft {
  return {
    evidence: "",
    label: relationshipLabelForType("related_to"),
    strength: 0.6,
    targetNodeId: frame?.graph.nodes.find((candidate) => candidate.id !== sourceNode?.id)?.id ?? "",
    type: "related_to"
  };
}

export function relationshipLabelForType(type: GraphRelationType) {
  return stableRelationshipLabels[type];
}

export function relationshipEditDraftFromEdge(edge: GraphEdge | null): RelationshipEditDraft {
  return {
    evidence: edge?.evidence ?? "",
    label: edge?.label ?? relationshipLabelForType(edge?.type ?? "related_to"),
    strength: edge?.strength ?? 0.6,
    type: edge?.type ?? "related_to"
  };
}

export function hasRelationshipEditChanges(edge: GraphEdge, draft: RelationshipEditDraft) {
  return (
    edge.type !== draft.type ||
    edge.label !== relationshipEditLabel(draft) ||
    (edge.evidence ?? "") !== draft.evidence.trim() ||
    edge.strength !== clampStrength(draft.strength)
  );
}

export function relationshipCreateChangeSetWrite(
  frame: GraphVisualizationFrame,
  sourceNode: GraphNode,
  draft: RelationshipCreateDraft
): GraphChangeSetWrite | null {
  const targetNode = frame.graph.nodes.find((candidate) => candidate.id === draft.targetNodeId);
  if (!targetNode || targetNode.id === sourceNode.id) {
    return null;
  }

  const type = draft.type;
  const label = draft.label.trim() || relationshipLabelForType(type);
  const evidence = draft.evidence.trim();
  const strength = clampStrength(draft.strength);
  const edgeId = relationshipEdgeId(sourceNode, targetNode, type);

  return {
    reason: stableRelationshipCreateReason(sourceNode.label, targetNode.label),
    clientMutationId: `relationship-create:${stableEditHash(edgeId)}:${stableEditHash(
      `${label}\n${evidence}\n${strength}`
    )}`.slice(0, 128),
    commands: [
      {
        type: "upsert_edge",
        edge: {
          id: edgeId,
          source: sourceNode.id,
          target: targetNode.id,
          type,
          label,
          confidence: 0.8,
          ...(evidence ? { evidence } : {}),
          strength,
          data: {}
        }
      }
    ]
  };
}

export function relationshipEditChangeSetWrite(edge: GraphEdge, draft: RelationshipEditDraft): GraphChangeSetWrite | null {
  if (!hasRelationshipEditChanges(edge, draft)) {
    return null;
  }

  const label = relationshipEditLabel(draft);
  const evidence = draft.evidence.trim();
  const strength = clampStrength(draft.strength);
  const { evidence: _oldEvidence, ...baseEdge } = edge;

  return {
    reason: stableRelationshipEditReason(edge.label),
    clientMutationId: `relationship-edit:${edge.id}:${stableEditHash(
      `${draft.type}\n${label}\n${evidence}\n${strength}`
    )}`.slice(0, 128),
    commands: [
      {
        type: "upsert_edge",
        edge: {
          ...baseEdge,
          type: draft.type,
          label,
          ...(evidence ? { evidence } : {}),
          strength
        }
      }
    ]
  };
}

export function relationshipDeleteChangeSetWrite(edge: GraphEdge): GraphChangeSetWrite {
  return {
    reason: stableRelationshipDeleteReason(edge.label),
    clientMutationId: `relationship-delete:${edge.id}:${stableEditHash(`${edge.source}\n${edge.target}`)}`.slice(0, 128),
    commands: [
      {
        type: "delete_edge",
        edgeId: edge.id
      }
    ]
  };
}

export function relationshipTouchpointChangeSetWrite(edge: GraphEdge, touchedAt: string): GraphChangeSetWrite {
  return {
    reason: stableRelationshipTouchpointReason(edge.label),
    clientMutationId: `relationship-touchpoint:${edge.id}:${stableEditHash(touchedAt)}`.slice(0, 128),
    commands: [
      {
        type: "upsert_edge",
        edge: {
          ...edge,
          data: { ...edge.data, lastTouchedAt: touchedAt }
        }
      }
    ]
  };
}

function relationshipEditLabel(draft: RelationshipEditDraft) {
  return draft.label.trim() || relationshipLabelForType(draft.type);
}

function relationshipEdgeId(sourceNode: GraphNode, targetNode: GraphNode, type: GraphRelationType) {
  return `${edgeIdSegment(sourceNode.label || sourceNode.id)}-${type}-${edgeIdSegment(targetNode.label || targetNode.id)}-${stableEditHash(
    `${sourceNode.id}:${type}:${targetNode.id}`
  )}`.slice(0, 180);
}

function edgeIdSegment(value: string) {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || `node-${stableEditHash(value)}`;
}

function clampStrength(value: number) {
  if (!Number.isFinite(value)) {
    return 0.6;
  }

  return Math.min(1, Math.max(0.1, Math.round(value * 100) / 100));
}

function stableEditHash(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(36);
}
